import { CpuChipIcon } from '@heroicons/react/24/outline';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AssessmentsAccumulationMappingsResponse,
  AssessmentsAccumulationResponse,
} from '@/entities/assessments/api/generated/model';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { Table, Text, WIDTH_PRESETS } from '@/shared/ui';
import { createAIAccumulationsTableColumns } from '../helpers/ai-accumulations-columns';

type AccumulationsTableProps = {
  /** 積算明細データ */
  accumulationData: AssessmentsAccumulationResponse | AssessmentsAccumulationMappingsResponse;
  /** 基準年度 */
  baseFiscalYear: number;
  /** 比較年度 */
  comparedFiscalYear: number;
  /** 金額単位 */
  amountUnit: AmountUnit;
  /** サイドパネル表示かどうか */
  isSidePanelView?: boolean;
};

/**
 * 差分用の積算明細テーブルコンポーネント
 */
export const AIAccumulationsTable: FC<AccumulationsTableProps> = ({
  accumulationData,
  baseFiscalYear,
  comparedFiscalYear,
  amountUnit,
  isSidePanelView = false,
}) => {
  const { t } = useTranslation();

  const columns = createAIAccumulationsTableColumns({
    baseFiscalYear,
    comparedFiscalYear,
    amountUnit,
    t,
  });

  return (
    <div
      className={isSidePanelView ? undefined : 'grid w-full'}
      style={isSidePanelView ? undefined : { gridTemplateColumns: `${WIDTH_PRESETS.xxs}px 1fr` }}
    >
      {!isSidePanelView && <div className="border-border-strength1 border-r bg-surface-base" />}
      <Table
        columns={columns}
        data={accumulationData.mappings}
        pagination={null}
        showLastBorderBottom={isSidePanelView}
        emptyRender={
          <div className="flex h-[180px] w-full flex-col items-center justify-center gap-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <CpuChipIcon className="h-6 w-6 text-text-accent" />
            </div>
            <Text variant="body" className="font-medium">
              {t('assessment.messages.ai-empty-title')}
            </Text>
            <Text variant="body" className="text-text-secondary">
              {t('assessment.messages.ai-empty-description')}
            </Text>
          </div>
        }
      />
    </div>
  );
};
